import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Activity, FileText, Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const NTEPPage = () => {
  const { api, user } = useAuth();
  const [analises, setAnalises] = useState([]);
  const [empregados, setEmpregados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnalise, setSelectedAnalise] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState({
    justificativa_empregado: '',
    relato_encarregado: '',
    funcao_exercida: ''
  });

  useEffect(() => {
    fetchAnalises();
    fetchEmpregados();
  }, []);

  const fetchAnalises = async () => {
    try {
      const response = await api.get('/ntep/analises');
      setAnalises(response.data);
    } catch (error) {
      console.error('Error fetching analises:', error);
      toast.error('Erro ao carregar análises NTEP');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados');
      setEmpregados(response.data);
    } catch (error) {
      console.error('Error fetching empregados:', error);
    }
  };

  const handleOpenAnalise = (analise) => {
    setSelectedAnalise(analise);
    setFormData({
      justificativa_empregado: analise.justificativa_empregado || '',
      relato_encarregado: analise.relato_encarregado || '',
      funcao_exercida: analise.funcao_exercida || ''
    });
    setDialogOpen(true);
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/ntep/analises/${selectedAnalise.id}`, null, { params: formData });
      toast.success('Análise atualizada!');
      fetchAnalises();
    } catch (error) {
      toast.error('Erro ao atualizar análise');
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const response = await api.post(`/ntep/analises/${selectedAnalise.id}/gerar-relatorio`);
      toast.success('Relatório gerado com sucesso!');
      setSelectedAnalise({
        ...selectedAnalise,
        relatorio_ia: response.data.relatorio
      });
      fetchAnalises();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar relatório');
    } finally {
      setGenerating(false);
    }
  };

  const getEmpregadoNome = (id) => {
    const emp = empregados.find(e => e.id === id);
    return emp ? emp.nome : 'N/A';
  };

  const getStatusBadge = (status) => {
    const config = {
      aberto: { label: 'Aberto', className: 'bg-warning' },
      relatorio_gerado: { label: 'Relatório Gerado', className: 'bg-info' },
      concluido: { label: 'Concluído', className: 'bg-accent' }
    };
    const c = config[status] || config.aberto;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ntep-page">
      <div>
        <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Análise NTEP</h2>
        <p className="text-muted-foreground">Nexo Técnico Epidemiológico Previdenciário</p>
      </div>

      <Card className="border-slate-100 bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-info/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Activity className="w-6 h-6 text-info" />
            </div>
            <div>
              <h3 className="font-semibold mb-1">O que é NTEP?</h3>
              <p className="text-sm text-muted-foreground">
                O Nexo Técnico Epidemiológico Previdenciário relaciona automaticamente doenças (CID) 
                com atividades econômicas (CNAE) através de dados estatísticos. Quando há coincidência,
                presume-se que a doença é ocupacional, permitindo impugnação mediante comprovação.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="text-lg font-['Outfit']">Análises Ativas</CardTitle>
        </CardHeader>
        <CardContent>
          {analises.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhuma análise NTEP ativa</p>
              <p className="text-sm text-muted-foreground mt-1">
                Análises são criadas automaticamente quando há coincidência CID x CNAE em atestados
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empregado</TableHead>
                    <TableHead>CID</TableHead>
                    <TableHead>CNAE</TableHead>
                    <TableHead>Coincidência</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analises.map((analise) => (
                    <TableRow key={analise.id} data-testid={`analise-row-${analise.id}`}>
                      <TableCell className="font-medium">{getEmpregadoNome(analise.empregado_id)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">{analise.cid}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{analise.cnae}</TableCell>
                      <TableCell>
                        <Badge className="bg-destructive">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Sim
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(analise.status)}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleOpenAnalise(analise)}
                          data-testid={`ver-analise-${analise.id}`}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Analisar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Análise NTEP - {selectedAnalise?.cid}</DialogTitle>
            <DialogDescription>
              Processo de impugnação de nexo técnico epidemiológico
            </DialogDescription>
          </DialogHeader>
          
          {selectedAnalise && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">CID</p>
                  <p className="font-semibold">{selectedAnalise.cid}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">CNAE da Empresa</p>
                  <p className="font-semibold">{selectedAnalise.cnae}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="funcao">Função Exercida</Label>
                  <Textarea
                    id="funcao"
                    value={formData.funcao_exercida}
                    onChange={(e) => setFormData({...formData, funcao_exercida: e.target.value})}
                    placeholder="Descreva a função exercida pelo empregado..."
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="justificativa">Justificativa do Empregado</Label>
                  <Textarea
                    id="justificativa"
                    value={formData.justificativa_empregado}
                    onChange={(e) => setFormData({...formData, justificativa_empregado: e.target.value})}
                    placeholder="Relato do empregado sobre as causas..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="relato">Relato do Encarregado</Label>
                  <Textarea
                    id="relato"
                    value={formData.relato_encarregado}
                    onChange={(e) => setFormData({...formData, relato_encarregado: e.target.value})}
                    placeholder="Relato do encarregado/supervisor..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleUpdate} variant="outline">
                  Salvar Informações
                </Button>
                <Button 
                  onClick={handleGenerateReport}
                  disabled={generating}
                  className="bg-accent hover:bg-accent/90"
                  data-testid="gerar-relatorio-btn"
                >
                  <Sparkles className={`w-4 h-4 mr-2 ${generating ? 'animate-pulse' : ''}`} />
                  {generating ? 'Gerando com IA...' : 'Gerar Relatório com IA'}
                </Button>
              </div>

              {selectedAnalise.relatorio_ia && (
                <div className="space-y-2">
                  <Label>Relatório Técnico de Impugnação (Gerado por IA)</Label>
                  <div className="p-4 bg-slate-50 rounded-lg border max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{selectedAnalise.relatorio_ia}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NTEPPage;
